import React from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Chip,
  Skeleton,
  Divider,
} from '@mui/material'
import {
  AttachMoney as MoneyIcon,
  PhoneAndroid as PhoneIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'

const RecentTransactions = ({ transactions = [], isLoading = false }) => {
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase':
        return <MoneyIcon />
      case 'rental':
        return <PhoneIcon />
      default:
        return <TrendingUpIcon />
    }
  }

  const getTransactionColor = (type) => {
    switch (type) {
      case 'purchase':
        return 'success'
      case 'rental':
        return 'primary'
      default:
        return 'default'
    }
  }

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'purchase':
        return 'Kredi Satın Alma'
      case 'rental':
        return 'SMS Kiralama'
      default:
        return 'İşlem'
    }
  }

  const formatAmount = (amount, type) => {
    if (type === 'purchase') {
      return `+$${parseFloat(amount).toFixed(2)}`
    } else if (type === 'rental') {
      return `-${parseInt(Math.abs(amount))} kredi`
    }
    return amount
  }

  if (isLoading) {
    return (
      <List>
        {[...Array(5)].map((_, index) => (
          <ListItem key={index} sx={{ px: 0 }}>
            <ListItemAvatar>
              <Skeleton variant="circular" width={40} height={40} />
            </ListItemAvatar>
            <ListItemText
              primary={<Skeleton variant="text" width="60%" />}
              secondary={<Skeleton variant="text" width="40%" />}
            />
            <Skeleton variant="rectangular" width={60} height={24} />
          </ListItem>
        ))}
      </List>
    )
  }

  if (!transactions.length) {
    return (
      <Box 
        sx={{ 
          textAlign: 'center', 
          py: 4,
          color: 'text.secondary'
        }}
      >
        <Typography variant="body2">
          Henüz işlem bulunmuyor
        </Typography>
      </Box>
    )
  }

  return (
    <List sx={{ p: 0 }}>
      {transactions.slice(0, 5).map((transaction, index) => (
        <React.Fragment key={transaction.id || index}>
          <ListItem
            sx={{
              px: 0,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1,
              },
            }}
          >
            <ListItemAvatar>
              <Avatar
                sx={{
                  backgroundColor: `${getTransactionColor(transaction.type)}.light`,
                  color: `${getTransactionColor(transaction.type)}.main`,
                  width: 40,
                  height: 40,
                }}
              >
                {getTransactionIcon(transaction.type)}
              </Avatar>
            </ListItemAvatar>
            
            <ListItemText
              primary={
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {transaction.user_email || transaction.userEmail || 'Kullanıcı'}
                </Typography>
              }
              secondary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={getTransactionLabel(transaction.type)}
                    size="small"
                    color={getTransactionColor(transaction.type)}
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {transaction.created_at 
                      ? formatDistanceToNow(parseISO(transaction.created_at), { 
                          addSuffix: true, 
                          locale: tr 
                        })
                      : 'Bilinmiyor'
                    }
                  </Typography>
                </Box>
              }
            />
            
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: transaction.type === 'purchase' ? 'success.main' : 'primary.main',
                }}
              >
                {formatAmount(
                  transaction.amount_in_credits || transaction.amount || 0,
                  transaction.type
                )}
              </Typography>
              
              {transaction.external_transaction_id && (
                <Typography variant="caption" color="text.secondary">
                  ID: {transaction.external_transaction_id.slice(-8)}
                </Typography>
              )}
            </Box>
          </ListItem>
          
          {index < transactions.slice(0, 5).length - 1 && (
            <Divider variant="inset" component="li" sx={{ ml: 7 }} />
          )}
        </React.Fragment>
      ))}
    </List>
  )
}

export default RecentTransactions